import React from 'react';

import { Typography } from 'ucl/components/text/Typography';

import { useFormatCalories } from 'hooks/use-format-calories';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';

import { ComposedCartItemType } from './hooks/use-compose-cart-item-ui-data';

interface CartItemPriceAndCaloriesProps {
  cartItem: ComposedCartItemType;
  isSingleItem: boolean;
}

// Temporarily suppressing calorie information on the frontend due to legal concerns.
// This will be reverted once the issue is resolved.
// See: https://rbidigital.slack.com/archives/C05DZTFFRQT/p1731961914027219 for more details.
const HIDE_CALORIES = true;

export const CartItemPriceAndCalories: React.FC<CartItemPriceAndCaloriesProps> = ({
  cartItem,
  isSingleItem,
}) => {
  const { modifiers, calories, price } = cartItem;

  const { formatPriceWithSign } = useUIContext();
  const formatCalories = useFormatCalories();

  // RegEx to cleanup multiple whitespaces and trim the string.
  const modifierName =
    modifiers
      ?.map(modifier => modifier.name?.trim())
      .filter(Boolean)
      .join(', ')
      ?.replace(/\s+/g, ' ')
      .trim() ?? '';

  const modifierPrice = modifiers?.reduce((acc, modifier) => acc + (modifier.price ?? 0), 0) ?? 0;
  const childPrice = price ?? 0;

  const cartItemCalories = calories ?? 0;
  const cartItemPrice = isSingleItem ? modifierPrice : childPrice + modifierPrice;

  const priceLabel = formatPriceWithSign(cartItemPrice);

  const caloriesLabel = HIDE_CALORIES ? '' : formatCalories(cartItemCalories);

  // When calories are hidden, show ONLY priceLabel.
  // When calories are enabled, preserve the original "price | calories" behavior.
  let displayLabel: string | undefined;

  if (HIDE_CALORIES) {
    displayLabel = priceLabel;
  } else if (priceLabel && caloriesLabel) {
    displayLabel =
      priceLabel && caloriesLabel
        ? `${priceLabel}${caloriesLabel ? ' | ' : ''}${caloriesLabel}`
        : undefined;
  }

  if (!displayLabel && !modifierName) {
    return null;
  }

  return (
    <>
      {displayLabel && <Typography fontSize="xs">{displayLabel}</Typography>}
      {modifierName && (
        <Typography fontSize="xs" style={{ flex: 1 }}>
          <Typography bold>{t('yourWay') + ' '}</Typography>
          {modifierName}
        </Typography>
      )}
    </>
  );
};
